import { useEffect, useState } from "react";
import { Spin } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import PageHeader from './PageHeader';
import { useTranslation } from 'react-i18next';
import "./VillageSectionCardDetails.css";
import axiosInstance from "../services/axios";
import PlaceCard from "./PlaceCard";
import VillageIllustration from "./VillageIllustration";
import EmptyState from './EmptyState';

// Section tag aliases mapping (maps old URL slugs to DB section names)
const SECTION_NAME_ALIASES = {
  'garden-area': 'tourist',
  'gram-panchayat': 'temple',
  'panchayat-building': 'water',
  'construction-work': 'education',
  'market-area': 'development',
  'local-programs': 'smallbusiness',
  'administrative-area': 'administration',
};

// Section tag to Marathi display name mapping
const sectionMapMr = {
  administration: "प्रशासकीय सेवा",
  development: "विकास कार्य",
  education: "शिक्षण प्रणाली",
  smallbusiness: "लघु उद्योग",
  temple: "गावातील मंदिरे",
  tourist: "पर्यटन स्थळे",
  water: "पाण्याची सुविधा"
};

// Section tag to English display name mapping
const sectionMapEn = {
  administration: "Administrative Services",
  development: "Development Work",
  education: "Education System",
  smallbusiness: "Small Industries",
  temple: "Village Temples",
  tourist: "Tourist Places",
  water: "Water Facilities"
};

const TouristList = ({ items }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { i18n } = useTranslation();

  // Get data from navigation state or use sample data
  const cardData = location.state?.cardData;

  // Get section name from URL params for breadcrumb
  const sectionParam = params.section || '';
  const sectionName = sectionParam.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  // Function to convert route param back to display name
  const getDisplayName = (param) => {
    const canonicalName = SECTION_NAME_ALIASES[param] || param;
    const currentMap = i18n.language === 'mr' ? sectionMapMr : sectionMapEn;
    return currentMap[canonicalName] || currentMap[param] || sectionName;
  };

  const displaySectionName = getDisplayName(sectionParam);

  // API-driven items
  const [apiItems, setApiItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVillageInfo = async () => {
      setLoading(true);
      setError(null);

      if (!sectionParam) {
        setApiItems(null);
        setLoading(false);
        return;
      }

      try {
        const language = i18n.language === 'mr' ? "Marathi" : "English";
        const canonicalName = SECTION_NAME_ALIASES[sectionParam] || sectionParam;

        // First, fetch village sections to get the section ID
        const villageIds = await axiosInstance.get('village-sections/', {
          params: { language }
        });

        const section = (villageIds.data || []).find(
          item => item.name === canonicalName || item.name === sectionParam
        );
        if (!section) {
          setApiItems(null);
          setLoading(false);
          return;
        }

        // Fetch village info using the section ID
        let response = await axiosInstance.get('/village-info/', {
          params: { tags: section.id, language }
        });

        let data = response.data || [];
        if (!Array.isArray(data) || data.length === 0) {
          // Fallback fetch without language parameter
          const fallbackResponse = await axiosInstance.get('/village-info/', {
            params: { tags: section.id }
          });
          data = fallbackResponse.data || [];
        }

        if (Array.isArray(data) && data.length > 0) {
          const mapped = data.map(d => ({
            title: d.title || '',
            description: d.description || '',
            photo: d.image || ''
          }));
          setApiItems(mapped);
        } else {
          setApiItems(null);
        }
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message || 'Error');
      } finally {
        setLoading(false);
      }
    };
    fetchVillageInfo();
  }, [i18n.language, sectionParam]);

  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  // Breadcrumb items configuration for PageHeader: pass array of React nodes (not objects)
  const breadcrumbItems = [
    <Link key="home" to="/">{i18n.language === 'mr' ? 'मुखपृष्ठ' : 'Home'}</Link>,
    <Link key="village" to="/our-village">{i18n.language === 'mr' ? 'आपली पंचायत' : 'Our Taluk'}</Link>,
    <span key="section">{displaySectionName}</span>
  ];
  return (
    <>
      <div className="amache-page section-padding bg-image-section">
        <PageHeader
          breadcrumb={breadcrumbItems}
          title={cardData
            ? `${i18n.language === 'mr' ? 'आमचे पंचायत : ' : 'Our Taluk : '}${cardData.title}`
            : `${i18n.language === 'mr' ? 'आमचे पंचायत : ' : 'Our Taluk : '}${displaySectionName}`
          }
          onBack={handleBackClick}
          backLabel={i18n.language === 'mr' ? 'मागे' : 'Back'}
        />

        <main className="amache-container">
          <section
            className="amache-card"

          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
              </div>
            ) : error ? (
              <div style={{ padding: '50px 0' }}>
                <EmptyState description={i18n.language === 'mr' ? 'डेटा लोड करण्यात अयशस्वी' : 'Error loading data'} />
              </div>
            ) : !apiItems || apiItems.length === 0 ? (
              <div style={{ padding: '50px 0' }}>
                <EmptyState description={i18n.language === 'mr' ? 'डेटा लवकरच उपलब्ध होईल' : 'Data will be available soon'} />
              </div>
            ) : (
              apiItems.map((item, idx) => (
                <PlaceCard key={idx} item={item} idx={idx} i18n={i18n} />
              ))
            )}
          </section>

        </main>
      </div>
      <VillageIllustration />
    </>
  );
};

export default TouristList;